import { useEffect } from "react";

import {
  APP_VERSION,
  fetchRemoteAppVersion,
  isReloadBlocked,
  reloadOnceForNewVersion,
} from "@/utils/appVersion";

/**
 * Site-wide: on first load and when the tab becomes visible/focused, compare
 * this build's id to `/version.json`. On mismatch, hard-reload so users pick
 * up the new deploy without a manual refresh.
 *
 * No interval — a tiny JSON fetch on focus is enough for deploy catch-up, and
 * page-load-only would miss tabs left open across a deploy.
 *
 * A registered reload blocker (`registerReloadBlocker`) defers it to the next check.
 *
 * Disabled in Vite dev — HMR owns updates there, and a timestamp version would
 * thrash reloads.
 */
export function useSilentAppVersionReload(): void {
  useEffect(() => {
    if (import.meta.env.DEV) return;
    if (!APP_VERSION) return;

    let cancelled = false;

    const check = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      const remote = await fetchRemoteAppVersion();
      if (cancelled || isReloadBlocked()) return;
      reloadOnceForNewVersion(remote);
    };

    void check();

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void check();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);
}
